"use client";

import { apiGetPendingOrderStats } from "@/api/api";
import { ChevronRightIcon } from "@/components/event-detail/chevron-right-icon";
import { CancelAllConfirmDialog } from "@/components/order/cancel-all-confirm-dialog";
import EmptyState from "@/components/portfolio/empty-state";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { ChevronExpandIcon } from "@/components/ui/icons";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { cn } from "@/lib/utils";
import { useUserInfo } from "@/store/user-info";
import { navigateToOrderGuessDetail, useAppRouter } from "@/utils/router";
import { get } from "@/utils/utils";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { useMemo, useState } from "react";
import {
    type OrderItemLike,
    OrderPositionCard,
    type PositionItemLike,
} from "./positions-panel-components/OrderPositionCard";
import { useCancelOrder } from "./positions-panel-hooks/use-cancel-order";
import { useCashOut } from "./positions-panel-hooks/use-cash-out";
import { useOrdersData } from "./positions-panel-hooks/use-orders-data";
import { usePositionsData } from "./positions-panel-hooks/use-positions-data";

interface MultiGuessPositionsSectionsProps {
    guess: any;
    event: any;
}

export function MultiGuessPositionsSections( { guess, event }: MultiGuessPositionsSectionsProps ) {
    const router = useAppRouter();
    const { userId } = useUserInfo();
    const guessNo = get( guess, "guessNo", "" );
    const eventNo = get( event, "eventNo", "" );
    const [ isPositionsExpanded, setIsPositionsExpanded ] = useState( false );
    const [ isOpenOrdersExpanded, setIsOpenOrdersExpanded ] = useState( false );

    const hasPositions = get( guess, "userYesShareQuantity", 0 ) > 0 || get( guess, "userNoShareQuantity", 0 ) > 0;
    const hasPendingOrders = get( guess, "userPendingOrderCount", 0 ) > 0;

    const { positionItems, isLoading: isPositionsLoading } = usePositionsData( guessNo, userId, isPositionsExpanded );
    const { ordersItems, isLoading: isOrdersLoading } = useOrdersData( guessNo, userId, isOpenOrdersExpanded );
    const { handleOpenCashOut } = useCashOut( event, guessNo, positionItems );
    const {
        confirmDialogOpen,
        setConfirmDialogOpen,
        isPending,
        cancelAllDialogOpen,
        setCancelAllDialogOpen,
        isPendingCancelAll,
        handleCancelOrder,
        handleConfirmCancel,
        handleCancelAll,
        handleConfirmCancelAll,
    } = useCancelOrder( guessNo, userId );

    const { data: cancelStatsData, isLoading: isLoadingCancelStats } = useQuery( {
        queryKey: [ "cancel-order-stats-guess", guessNo ],
        queryFn: async () => {
            const [ isOk, res ] = await apiGetPendingOrderStats( { guessNo } );
            if ( isOk ) return get( res, "data", null );
            throw new Error( get( res, "msg", "Failed to load stats" ) );
        },
        enabled: cancelAllDialogOpen && !!guessNo,
        staleTime: 0,
    } );

    const cancelAllSummary = useMemo( () => {
        let buyQty = 0;
        let buyValue = 0;
        let sellQty = 0;
        let sellValue = 0;

        const statsList = get( cancelStatsData, "statsList", [] );
        statsList.forEach( ( item: any ) => {
            if ( item.direction === 1 ) {
                buyQty = item.totalQuantity;
                buyValue = item.totalAmount;
            } else if ( item.direction === 2 ) {
                sellQty = item.totalQuantity;
                sellValue = item.totalAmount;
            }
        } );

        return { buyQty, buyValue, sellQty, sellValue };
    }, [ cancelStatsData ] );

    if ( !hasPositions && !hasPendingOrders ) {
        return null;
    }

    return (
        <>
            {hasPositions && (
                <>
                    <button
                        type="button"
                        onClick={() => setIsPositionsExpanded( prev => !prev )}
                        className="flex w-full items-center justify-between border-t border-stroke px-3 py-2 transition-colors"
                    >
                        <div className="flex items-center gap-0.5">
                            <span className="text-sm font-medium text-t1">Positions</span>
                            <ChevronExpandIcon
                                className={cn(
                                    "size-6 transition-transform duration-200",
                                    isPositionsExpanded && "rotate-180",
                                )}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            {positionItems.length >= 1 && (
                                <button
                                    type="button"
                                    className="text-xs font-medium text-t3 hover:opacity-80"
                                    onClick={( e ) => {
                                        e.stopPropagation();
                                        if ( guessNo && eventNo ) {
                                            navigateToOrderGuessDetail( router, { guessNo, eventNo } );
                                        }
                                    }}
                                >
                                    +{positionItems.length} More
                                </button>
                            )}
                        </div>
                    </button>

                    <AnimatePresence initial={false}>
                        {isPositionsExpanded && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{
                                    height: { duration: 0.3, ease: "easeInOut" },
                                    opacity: { duration: 0.2, ease: "easeInOut" },
                                }}
                                style={{ overflow: "hidden" }}
                            >
                                <div className="border-t border-stroke">
                                    {isPositionsLoading
                                        ? (
                                            <div className="flex items-center justify-center p-3 py-8">
                                                <LoadingSpinner />
                                            </div>
                                        )
                                        : positionItems.length === 0
                                        ? (
                                            <EmptyState
                                                text="No positions"
                                                className="h-auto py-8"
                                                textClassName="text-sm"
                                            />
                                        )
                                        : (
                                            <div className="flex flex-col gap-2 px-3 py-3">
                                                {positionItems.slice( 0, 2 ).map( ( item ) => (
                                                    <OrderPositionCard
                                                        key={item.key}
                                                        type="position"
                                                        item={item as PositionItemLike}
                                                        currentGuess={guess}
                                                        guessNo={guessNo}
                                                        eventNo={eventNo}
                                                        onSell={( current ) =>
                                                            handleOpenCashOut( current as PositionItemLike )}
                                                        isSports={false}
                                                    />
                                                ) )}
                                            </div>
                                        )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </>
            )}

            {hasPendingOrders && (
                <>
                    <button
                        type="button"
                        onClick={() => setIsOpenOrdersExpanded( prev => !prev )}
                        className="flex w-full items-center justify-between border-t border-stroke px-3 py-2 transition-colors"
                    >
                        <div className="flex items-center gap-0.5">
                            <span className="text-sm font-medium text-t1">Open Orders</span>
                            <ChevronExpandIcon
                                className={cn(
                                    "size-6 transition-transform duration-200",
                                    isOpenOrdersExpanded && "rotate-180",
                                )}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            {ordersItems.length >= 1 && (
                                <button
                                    type="button"
                                    className="text-xs font-medium text-state-danger hover:opacity-80 disabled:opacity-50"
                                    disabled={isPendingCancelAll}
                                    onClick={( e ) => {
                                        e.stopPropagation();
                                        handleCancelAll();
                                    }}
                                >
                                    Cancel All
                                </button>
                            )}
                        </div>
                    </button>

                    <AnimatePresence initial={false}>
                        {isOpenOrdersExpanded && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{
                                    height: { duration: 0.3, ease: "easeInOut" },
                                    opacity: { duration: 0.2, ease: "easeInOut" },
                                }}
                                style={{ overflow: "hidden" }}
                            >
                                <div className="border-t border-stroke">
                                    {isOrdersLoading
                                        ? <div className="p-3 py-8 text-center text-sm text-t3">Loading...</div>
                                        : ordersItems.length === 0
                                        ? (
                                            <EmptyState
                                                text="No open orders"
                                                className="h-auto py-8"
                                                textClassName="text-sm"
                                            />
                                        )
                                        : (
                                            <div className="flex flex-col gap-2 px-3 py-3">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs font-medium text-t3">Order Qty:</span>
                                                    <button
                                                        type="button"
                                                        className="flex items-center text-xs font-medium text-t3 hover:opacity-80"
                                                        onClick={() => {
                                                            if ( guessNo && eventNo ) {
                                                                navigateToOrderGuessDetail( router, {
                                                                    guessNo,
                                                                    eventNo,
                                                                } );
                                                            }
                                                        }}
                                                    >
                                                        +{ordersItems.length} More
                                                        <ChevronRightIcon className="size-4 text-t3" />
                                                    </button>
                                                </div>
                                                {ordersItems.slice( 0, 2 ).map( ( order: any, index: number ) => (
                                                    <OrderPositionCard
                                                        key={get( order, "orderNo", index )}
                                                        type="order"
                                                        item={order as OrderItemLike}
                                                        currentGuess={guess}
                                                        guessNo={guessNo}
                                                        eventNo={eventNo}
                                                        onCancel={( current ) =>
                                                            handleCancelOrder( current as OrderItemLike )}
                                                        isSports={false}
                                                    />
                                                ) )}
                                            </div>
                                        )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </>
            )}

            <ConfirmDialog
                open={confirmDialogOpen}
                onOpenChange={setConfirmDialogOpen}
                title="Are you sure?"
                description="Are you sure you want to cancel this open order?"
                confirmText="Confirm"
                cancelText="Never mind"
                onConfirm={handleConfirmCancel}
                isLoading={isPending}
            />

            <CancelAllConfirmDialog
                open={cancelAllDialogOpen}
                onOpenChange={setCancelAllDialogOpen}
                summary={cancelAllSummary}
                onConfirm={handleConfirmCancelAll}
                isLoading={isPendingCancelAll}
                isLoadingStats={isLoadingCancelStats}
            />
        </>
    );
}
